import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { messagingApi } from '../../api/messagingApi';
import { AuthContext } from '../../context/AuthContext';
import { NotificationContext } from '../../context/NotificationContext';
import { 
  Box, 
  TextField, 
  Button, 
  CircularProgress, 
  Typography, 
  Paper, 
  Avatar, 
  Tooltip, 
  IconButton, 
  Modal, 
  Backdrop, 
  Fade,
  Card,
  CardMedia
} from '@mui/material';
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'; 
import { API } from '../../api/axios';
import { linkifyText } from '../../utils/linkify';

// Normalize messages from backend
const normalizeMessage = (msg) => {
  let attachments = [];
  if (Array.isArray(msg.attachments)) {
    attachments = msg.attachments;
  } else if (typeof msg.attachments === 'string') {
    try {
      const parsed = JSON.parse(msg.attachments);
      if (Array.isArray(parsed)) {
        attachments = parsed;
      }
    } catch (e) {
      // Not a valid JSON string or not an array after parsing.
      // attachments remains an empty array.
    }
  }

  return {
    ...msg,
    id: msg.id || msg._id,
    messageText: msg.messageText || msg.message || msg.text || '',
    createdAt: msg.createdAt || msg.created_at || msg.timestamp || new Date().toISOString(),
    senderId: msg.senderId || msg.sender?.id,
    recipientId: msg.recipientId || msg.recipient?.id,
    attachments: attachments,
  };
};

const ConversationView = ({ conversation, onConversationCreated }) => {
  const sentBubbleColor = '#6e0506';
  const receivedBubbleColor = '#f5f5f5';
  const receivedBubbleTextColor = '#2b2b2b';
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [showNewMessageButton, setShowNewMessageButton] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]); // New state for selected files
  const [openModal, setOpenModal] = useState(false); // New state for modal
  const [selectedImage, setSelectedImage] = useState(null); // New state for selected image in modal

  const { userId } = useContext(AuthContext);
  const { latestMessage } = useContext(NotificationContext);

  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null); // New ref for file input

  /** ---------------- Scroll Helpers ---------------- */
  // Check if user is near the bottom
  const isUserNearBottom = () => {
    const container = containerRef.current;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < 120;
  };

  // Scroll to bottom smoothly
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowNewMessageButton(false);
  };

  // Scroll to bottom instantly on load
  const scrollToBottomOnLoad = () => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    });
  };

  /** ---------------- Fetch Messages ---------------- */
  const fetchMessages = useCallback(async () => {
    if (!conversation?.otherUser) {
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await messagingApi.getConversation(conversation.castingId, conversation.otherUser.id);
      if (res.data.success) {
        let fetchedMessages = res.data.data.map(normalizeMessage);

        // Append latestMessage if relevant
        if (latestMessage) {
          const recipientId = conversation.otherUser.id;
          const isRelevant =
            latestMessage.castingId === conversation.castingId &&
            ((latestMessage.senderId === userId && latestMessage.recipientId === recipientId) ||
             (latestMessage.senderId === recipientId && latestMessage.recipientId === userId));

          if (isRelevant && !fetchedMessages.some(m => m.id === latestMessage.id)) {
            fetchedMessages.push(normalizeMessage(latestMessage));
          }
        }
        

        setMessages(fetchedMessages);
        scrollToBottomOnLoad();
      } else {
        setError(res.data.message || 'Failed to load messages.');
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages.');
    } finally {
      setLoading(false);
    }
  }, [conversation, latestMessage, userId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  /** ---------------- Listen to new messages ---------------- */
  useEffect(() => {
    if (!latestMessage || !conversation?.otherUser) return;

    const recipientId = conversation.otherUser.id;
    const isRelevant =
      latestMessage.castingId === conversation.castingId &&
      ((latestMessage.senderId === userId && latestMessage.recipientId === recipientId) ||
       (latestMessage.senderId === recipientId && latestMessage.recipientId === userId));

    if (!isRelevant) return;

    setMessages(prev => {
      if (prev.some(m => m.id === latestMessage.id)) return prev;
      return [...prev, normalizeMessage(latestMessage)];
    });

    // Scroll if user is at bottom
    if (isUserNearBottom()) scrollToBottom();
    else setShowNewMessageButton(true);
  }, [latestMessage, conversation, userId]);

  /** ---------------- File Upload Handlers ---------------- */
  const handleFileChange = (e) => {
    setSelectedFiles([...selectedFiles, ...e.target.files]);
  };

  const removeFile = (fileName) => {
    setSelectedFiles(selectedFiles.filter((file) => file.name !== fileName));
  };

  const handleOpenModal = (attachment) => {
    setSelectedImage(attachment); // Store the full attachment object
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedImage(null);
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await fetch(attachment.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = attachment.name || "download";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const containsDisallowedMediaLink = (text = "") => {
    const normalized = text.toLowerCase();
    return /(?:https?:\/\/|www\.)?[^\s]+\.(mp3|mp4)(?:[?#][^\s]*)?/i.test(normalized)
      || /(?:^|\s)(?:[a-z0-9.-]+\.)+(?:mp3|mp4)(?:[/?#][^\s]*)?/i.test(normalized);
  };

  /** ---------------- Send Message ---------------- */
  const handleSendMessage = async () => {
    const trimmedMessage = newMessage.trim();
    if ((!trimmedMessage && selectedFiles.length === 0) || !otherUser) return;

    if (containsDisallowedMediaLink(trimmedMessage)) {
      setError("MP3 and MP4 links are not allowed in messages.");
      return;
    }

    setSending(true);
    setError(null);

    const recipientId = conversation.otherUser.id;
    const tempId = `temp-${Date.now()}`;

    const tempMessage = {
      id: tempId,
      messageText: newMessage.trim(),
      senderId: userId,
      sender: { id: userId }, // Minimal sender info for optimistic update
      createdAt: new Date().toISOString(),
      attachments: selectedFiles.map((file) => ({
        url: URL.createObjectURL(file),
        name: file.name,
        type: file.type,
      })),
    };

    setMessages(prev => [...prev, tempMessage]);
    setNewMessage('');
    const filesToSend = [...selectedFiles];
    setSelectedFiles([]);
    scrollToBottom();


    try {
      const res = await messagingApi.sendMessage(
        conversation.castingId, 
        recipientId, 
        tempMessage.messageText, 
        filesToSend
      );

      if (res.data.success && res.data.data) {
        const normalized = normalizeMessage(res.data.data);
        setMessages(prev => prev.map(msg => msg.id === tempId ? normalized : msg));
        
        if (onConversationCreated && !conversation.otherUser) {
          onConversationCreated({
            ...conversation,
            otherUser: conversation.otherUser,
            castingId: conversation.castingId,
            castingTitle: conversation.castingTitle,
            messages: [normalized],
          });
        }
      } else {
        setMessages(prev => prev.filter(msg => msg.id !== tempId)); // Revert optimistic update
        setError(res.data.message || 'Failed to send message.');
      }
    } catch (err) {
      console.error('Send message error:', err);
      setMessages(prev => prev.filter(msg => msg.id !== tempId)); // Revert optimistic update
      setError(err.response?.data?.message || 'Unexpected error.');
    } finally {
      setSending(false);
    }
  };

  const otherUser = conversation?.otherUser;
  const participantName = otherUser ? `${otherUser.firstName || ''} ${otherUser.lastName || ''}`.trim() || 'User' : 'User';
  const participantImage = otherUser?.imageURL || '';
  const DEFAULT_AVATAR = 'https://via.placeholder.com/40x40.png?text=User';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1, minHeight: 400, maxHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar src={participantImage || DEFAULT_AVATAR} alt={participantName}>
          {!participantImage && participantName[0]}
        </Avatar>
        <Box>
          <Typography variant="h6" noWrap>{conversation.castingTitle}</Typography>
          <Typography variant="subtitle2" color="textSecondary" noWrap>
            {otherUser ? `Chatting with ${participantName}` : `Start a new conversation with ${participantName}`}
          </Typography>
        </Box>
      </Paper>

      {/* Messages */}
      <Box
        ref={containerRef}
        sx={{ flex: 1, overflowY: 'auto', p: 1, mb: 1, display: 'flex', flexDirection: 'column' }}
        onScroll={() => {
          // Hide new message button if user scrolls to bottom manually
          if (isUserNearBottom()) setShowNewMessageButton(false);
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress /></Box>
        ) : error ? (
          <Typography color="error" sx={{ textAlign: 'center', mt: 2 }}>{error}</Typography>
        ) : messages.length === 0 ? (
          <Typography sx={{ textAlign: 'center', mt: 2 }}>Start a conversation!</Typography>
        ) : (
          messages.map(msg => (
            <Paper 
              key={msg.id} 
              sx={{
                p: 1.5,
                mb: 1,
                maxWidth: '70%',
                alignSelf: msg.senderId === userId ? 'flex-end' : 'flex-start',
                bgcolor: msg.senderId === userId ? sentBubbleColor : receivedBubbleColor,
                color: msg.senderId === userId ? '#ffffff' : receivedBubbleTextColor,
                wordBreak: 'break-word',
              }}
            >
              {msg?.attachments && msg?.attachments?.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  { msg?.attachments?.length && msg?.attachments?.map((att, index) => (
                    <Box key={index} sx={{ position: "relative" }}>
                      {att.type && att.type.startsWith("image/") ? (
                        <img
                          src={att.url}
                          alt={att.name || `attachment ${index}`}
                          style={{
                            maxWidth: "100%",
                            borderRadius: "8px",
                            marginTop: "8px",
                            cursor: "pointer",
                          }}
                          onClick={() => handleOpenModal(att)}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: '#e0e0e0',
                            p: 1,
                            borderRadius: '8px',
                            marginTop: '8px',
                          }}
                        >
                          <InsertDriveFileIcon />
                          <Typography variant="body2">{att.name || "File"}</Typography>
                        </Box>
                      )}
                      <IconButton
                        size="small"
                        sx={{
                          position: "absolute",
                          top: att.type && att.type.startsWith("image/") ? 8 : 4,
                          right: att.type && att.type.startsWith("image/") ? 8 : 4,
                          bgcolor: "rgba(0,0,0,0.5)",
                          color: "white",
                        }}
                        onClick={() => handleDownload(att)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography variant="body1">{linkifyText(msg.messageText)}</Typography>
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'right' }}>
                {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Paper>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* New message button */}
      {showNewMessageButton && (
        <Button
          variant="contained"
          color="secondary"
          sx={{ mb: 1, alignSelf: 'center' }}
          onClick={scrollToBottom}
        >
          New Message
        </Button>
      )}

      {/* Selected Files Preview */}
      {selectedFiles.length > 0 && (
        <Box sx={{ p: 1, display: "flex", gap: 1, overflowX: "auto" }}>
          {selectedFiles.map((file) => (
            <Card key={file.name} sx={{ position: "relative", width: 100, flexShrink: 0 }}>
              {file.type.startsWith("image/") ? (
                <CardMedia
                  component="img"
                  height="100"
                  image={URL.createObjectURL(file)}
                  alt={file.name}
                />
              ) : (
                <Box
                  sx={{
                    height: 100,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#f0f0f0',
                    p: 1,
                  }}
                >
                  <InsertDriveFileIcon fontSize="large" />
                  <Typography variant="caption" textAlign="center" sx={{ wordBreak: 'break-all' }}>
                    {file.name}
                  </Typography>
                </Box>
              )}
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bgcolor: "rgba(0,0,0,0.5)",
                  color: "white",
                }}
                onClick={() => removeFile(file.name)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Card>
          ))}
        </Box>
      )}

      {/* Input */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <Tooltip title="Attach file">
          <span>
            <IconButton onClick={() => fileInputRef.current.click()}>
              <AttachFileIcon />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type a message"
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          onKeyPress={e => e.key === 'Enter' && handleSendMessage()}
          disabled={sending}
        />
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSendMessage} 
          disabled={sending || (!newMessage.trim() && selectedFiles.length === 0)}
        >
          {sending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
        </Button>
      </Box>

      {/* Image Preview Modal */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              outline: "none",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            {selectedImage && selectedImage.url && selectedImage.type && selectedImage.type.startsWith("image/") ? (
              <img
                src={selectedImage.url}
                alt={selectedImage.name || "enlarged"}
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "80vh",
                  objectFit: "contain",
                }}
              />
            ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '200px',
                    bgcolor: '#f0f0f0',
                    p: 2,
                    textAlign: 'center',
                  }}
                >
                  <InsertDriveFileIcon fontSize="large" sx={{ mb: 1 }}/>
                  <Typography variant="h6">{selectedImage?.name || "File Preview Not Available"}</Typography>
                  <Typography variant="body2">Only images can be previewed in the modal. Please download the file.</Typography>
                </Box>
            )}

             <Button
                variant="contained"
                onClick={() => handleDownload(selectedImage)}
                sx={{ mt: 2 }}
                startIcon={<DownloadIcon />}
              >
                Download
              </Button>
            <IconButton
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
              }}
              onClick={handleCloseModal}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default ConversationView;
